import { useCallback, useEffect, useRef, useState } from 'react';

const WAITING = { data: null, error: null, updatedAt: null, loading: true };
const IDLE = { data: null, error: null, updatedAt: null, loading: false };

export default function usePolling(load, { intervalOf, enabled = true, resetKey = null }) {
  const [state, setState] = useState({ ...(enabled ? WAITING : IDLE), key: resetKey });
  const loadRef = useRef(load);
  const intervalOfRef = useRef(intervalOf);
  const runRef = useRef(() => {});

  useEffect(() => {
    loadRef.current = load;
    intervalOfRef.current = intervalOf;
  });

  useEffect(() => {
    if (!enabled) {
      setState({ ...IDLE, key: resetKey });
      return undefined;
    }
    let active = true;
    let timer = null;
    let controller = null;
    let latest = null;
    setState({ ...WAITING, key: resetKey });

    const stopTimer = () => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    };
    const schedule = () => {
      stopTimer();
      if (active && !document.hidden) {
        timer = setTimeout(run, intervalOfRef.current(latest));
      }
    };
    const run = async () => {
      stopTimer();
      controller?.abort();
      const current = new AbortController();
      controller = current;
      try {
        const data = await loadRef.current(current.signal);
        if (!active || controller !== current) return;
        latest = data;
        setState({ data, error: null, updatedAt: Date.now(), loading: false, key: resetKey });
      } catch (error) {
        if (!active || controller !== current) return;
        setState((previous) => ({ ...previous, error, loading: false }));
      }
      schedule();
    };
    const onVisibilityChange = () => {
      if (document.hidden) {
        stopTimer();
      } else {
        run();
      }
    };

    runRef.current = run;
    document.addEventListener('visibilitychange', onVisibilityChange);
    if (!document.hidden) {
      run();
    }
    return () => {
      active = false;
      stopTimer();
      controller?.abort();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      runRef.current = () => {};
    };
  }, [enabled, resetKey]);

  const refresh = useCallback(() => runRef.current(), []);
  const { key, ...current } = state.key === resetKey ? state : { ...(enabled ? WAITING : IDLE), key: resetKey };

  return { ...current, refresh };
}
